import { createSlice } from '@reduxjs/toolkit';
import { collection, getDocs, getFirestore, limit, onSnapshot, query, where } from 'firebase/firestore';
import { Unsubscribe } from 'firebase/firestore';
import { CompanyData, defaultCompanyData, formatCompanyData } from 'src/type';
import type { AppDispatch } from '../store';

// -------------------------
// リアルタイムリスナー管理
// -------------------------
let unsubscribeListener: Unsubscribe | null = null;

// 審査員ログイン用フォールバック。owner配列に含まれないuidの場合、
// companies.judgeUids にログインuidが含まれる会社を検索し、role:'judge' として返す。
async function resolveJudgeCompany(uid: string): Promise<CompanyData | null> {
  if (!uid) return null;
  const db = getFirestore();
  let judgeSnap;
  try {
    judgeSnap = await getDocs(query(
      collection(db, 'companies'),
      where('judgeUids', 'array-contains', uid),
      limit(1),
    ));
  } catch (e) {
    // Firestore Rules 未デプロイ等で companies(judgeUids) の list が
    // permission-denied になると、ここで例外が投げられ subscribeCompany の
    // onSnapshot コールバックが未処理のまま終わり、会社が永遠に解決されず
    // 画面が「読み込み中」で固まる。握りつぶして null を返し、呼び出し側で
    // defaultCompanyData を dispatch させる。
    console.error('resolveJudgeCompany failed:', e);
    return null;
  }
  if (judgeSnap.empty) return null;

  const companySnap = judgeSnap.docs[0];
  const companyData = companySnap.data();
  const judgeInvites = Array.isArray(companyData.judgeInvites) ? companyData.judgeInvites : [];
  const judge = judgeInvites.find((j: any) => j.uid === uid);
  return formatCompanyData({
    id: companySnap.id,
    ...companyData,
    role: 'judge',
    judgeName: judge?.name ?? '',
  });
}

export const subscribeCompany = (uid: string, _email: string) => (dispatch: AppDispatch) => {
  // 既存リスナーを解除
  if (unsubscribeListener) {
    unsubscribeListener();
    unsubscribeListener = null;
  }

  const db = getFirestore();
  const q = query(
    collection(db, 'companies'),
    where('owner', 'array-contains', uid),
    limit(1)
  );

  unsubscribeListener = onSnapshot(
    q,
    async (snapshot) => {
      if (snapshot.empty) {
        const judgeCompany = await resolveJudgeCompany(uid);
        dispatch(setCompany(judgeCompany ?? defaultCompanyData));
        return;
      }

      const companyDoc = snapshot.docs[0];
      dispatch(setCompany(formatCompanyData({ id: companyDoc.id, ...companyDoc.data() })));
    },
    (error) => {
      // onSnapshot 自体のエラー(owner list の permission-denied 等)。
      // ハンドラを付けないと "Uncaught Error in snapshot listener" だけが
      // コンソールに出て会社が解決されず画面が固まるため、審査員フォールバックを
      // 試み、それも駄目なら default を入れて描画を進める。
      console.error('subscribeCompany onSnapshot error:', error);
      resolveJudgeCompany(uid)
        .then((judgeCompany) => dispatch(setCompany(judgeCompany ?? defaultCompanyData)))
        .catch(() => dispatch(setCompany(defaultCompanyData)));
    },
  );
};

export const unsubscribeCompany = () => {
  if (unsubscribeListener) {
    unsubscribeListener();
    unsubscribeListener = null;
  }
};

// -------------------------
// state型
// -------------------------
interface CompanyState {
  company: CompanyData;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

// -------------------------
// 初期状態
// -------------------------
const initialState: CompanyState = {
  company: defaultCompanyData,
  status: 'idle',
};

// -------------------------
// slice
// -------------------------
const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setCompany: (state, action) => {
      state.company = action.payload;
      state.status = 'succeeded';
    },
  },
});

export const { setCompany } = companySlice.actions;
export default companySlice.reducer;
